import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { HierarchyNode } from '../../models/cost-analysis.models';

/**
 * Tiny status chip for a hierarchy row's <c>costMode</c>:
 * <c>R</c> (green) — every key in every entry came from a real run.
 * <c>M</c> (amber) — at least one Real and one Standard component.
 * <c>S</c> (grey)  — every key was synthesized from FabricColorStandardCost.
 *
 * Used inline next to fabric / roll / process labels across the cost-analysis
 * tabs. Keeps the visual weight low (1ch wide) so it doesn't compete with
 * the cost numbers.
 */
@Component({
  selector: 'app-cost-mode-chip',
  templateUrl: './cost-mode-chip.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CostModeChipComponent {
  readonly mode = input.required<HierarchyNode['costMode']>();

  readonly letter = computed(() => {
    switch (this.mode()) {
      case 'Real':
        return 'R';
      case 'Mixed':
        return 'M';
      case 'Standard':
        return 'S';
    }
  });

  readonly title = computed(() => {
    switch (this.mode()) {
      case 'Real':
        return 'Real — every cost component came from a real-cost run.';
      case 'Mixed':
        return 'Mixed — some components are real, others synthesized from FabricColorStandardCost.';
      case 'Standard':
        return 'Standard — every component synthesized from FabricColorStandardCost at read time.';
    }
  });

  readonly classes = computed(() => {
    const base = 'inline-flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-bold leading-none';
    switch (this.mode()) {
      case 'Real':
        return `${base} bg-[var(--p-green-100)] text-[var(--p-green-800)]`;
      case 'Mixed':
        return `${base} bg-[var(--p-amber-100)] text-[var(--p-amber-800)]`;
      case 'Standard':
        return `${base} bg-[var(--p-surface-200)] text-[var(--p-surface-700)]`;
    }
  });
}
